"use client";

import { handleError, trackCustomEvent } from "@/lib/newrelic";
import { cohesion } from "@redventures/cohesion-utils-react";
import { useEffect, useState } from "react";

interface MonarchTrafficFlow {
  value?: {
    experienceNumber?: string;
    experienceName?: string;
    experienceId?: string;
  };
  returnDetails?: {
    experienceId?: string;
    experienceName?: string;
    type?: string;
  };
  audienceName?: string;
  audienceId?: string;
  returnType?: string;
}

interface MonarchEvaluationResult {
  source?: {
    trafficFlow?: MonarchTrafficFlow;
    [key: string]: unknown;
  };
}

interface UseMonarchResult {
  globalRules: Record<string, unknown>;
  isMonarchLoading: boolean;
}

export default function useMonarch(): UseMonarchResult {
  const [globalRules, setGlobalRules] = useState<Record<string, unknown>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    cohesion("error", function () {
      console.error("[Cohesion] Failed to load");
      handleError(new Error("cohesion:failed: Cohesion failed to load"), {
        source: "cohesion",
        severity: "high",
      });
      setGlobalRules({});
      setIsLoading(false);
    });

    cohesion("monarch:done", (err: unknown, evaluation: unknown) => {
      if (err) {
        console.error("[Monarch] Error during evaluation:", err);
        handleError(err instanceof Error ? err : new Error(String(err)), {
          source: "monarch",
          severity: "high",
        });
        setGlobalRules({});
        setIsLoading(false);
        return;
      }

      const result = evaluation as MonarchEvaluationResult | null;
      const trafficFlow = result?.source?.trafficFlow;
      const source = (result?.source ?? {}) as Record<string, unknown>;
      const { trafficFlow: _trafficFlow, ...rawRules } = source;

      setGlobalRules(
        Object.fromEntries(
          Object.entries(rawRules).map(([ruleName, rule]) => [
            ruleName,
            (rule as { value?: unknown })?.value ?? rule,
          ])
        )
      );
      setIsLoading(false);

      if (trafficFlow) {
        const { experienceNumber, experienceName } = trafficFlow.value || {};
        const { audienceName } = trafficFlow;

        trackCustomEvent("monarchExperienceLoaded", {
          experienceNumber: experienceNumber || "",
          experienceName: experienceName || "",
          audienceName: audienceName || "",
        });

        if (process.env.NODE_ENV !== "production") {
          console.log("[Monarch] Experience loaded:", {
            experienceNumber,
            experienceName,
            audienceName,
          });
        }
      }
    });

    cohesion("ready", () => {
      const result = window?._Monarch?.evaluations
        ?.source as MonarchEvaluationResult;
      if (!result) {
        return;
      }
      const trafficFlow = result?.source?.trafficFlow;

      const data = {
        audienceId: String(trafficFlow?.audienceId ?? ""),
        audienceName: String(trafficFlow?.audienceName ?? ""),
        experienceId: String(trafficFlow?.value?.experienceId ?? ""),
        experienceName: String(trafficFlow?.value?.experienceName ?? ""),
        experienceNumber: String(trafficFlow?.value?.experienceNumber ?? ""),
        returnableId: String(trafficFlow?.returnDetails?.experienceId ?? ""),
        returnableName: String(
          trafficFlow?.returnDetails?.experienceName ?? ""
        ),
        returnableType: String(trafficFlow?.returnDetails?.type ?? ""),
        trafficFlowId: String(trafficFlow?.returnType ?? ""),
      };

      window?.fuse?.("meta", data);
    });
  }, []);

  return { globalRules, isMonarchLoading: isLoading };
}
